import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { PieChart, Pie, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import '../App.css';

function Dashboard() {
  const { hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [repairAnalytics, setRepairAnalytics] = useState(null);
  const [repairAnalyticsLoading, setRepairAnalyticsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'repairs' && hasPermission('canManageRepairs') && !repairAnalytics && !repairAnalyticsLoading) {
      fetchRepairAnalytics();
    }
  }, [activeTab, hasPermission, repairAnalytics, repairAnalyticsLoading]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/dashboard/overview');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairAnalytics = async () => {
    setRepairAnalyticsLoading(true);
    try {
      const [assetCosts, monthlyCosts, summary] = await Promise.all([
        axios.get('/api/repairanalytics/asset-repair-costs'),
        axios.get('/api/repairanalytics/monthly-repair-costs'),
        axios.get('/api/repairanalytics/repair-summary')
      ]);

      setRepairAnalytics({
        assetCosts: assetCosts.data,
        monthlyCosts: monthlyCosts.data,
        summary: summary.data
      });
    } catch (error) {
      console.error('Error fetching repair analytics:', error);
    } finally {
      setRepairAnalyticsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid dashboard-skeleton-page">
        <div className="row dashboard-stats">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="col-md-2 col-sm-4 col-6 mb-3">
              <div className="card h-100 dashboard-skeleton-card">
                <div className="card-body p-3">
                  <div className="dashboard-skeleton-line dashboard-skeleton-line-icon"></div>
                  <div className="dashboard-skeleton-line dashboard-skeleton-line-value mt-3"></div>
                  <div className="dashboard-skeleton-line dashboard-skeleton-line-label mt-2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="card dashboard-skeleton-card mb-4">
          <div className="card-body py-3">
            <div className="dashboard-skeleton-tabs">
              <div className="dashboard-skeleton-line dashboard-skeleton-line-tab"></div>
              <div className="dashboard-skeleton-line dashboard-skeleton-line-tab"></div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-4">
            <div className="card dashboard-skeleton-card">
              <div className="card-header">
                <div className="dashboard-skeleton-line dashboard-skeleton-line-heading"></div>
              </div>
              <div className="card-body">
                <div className="dashboard-skeleton-chart"></div>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div className="card dashboard-skeleton-card">
              <div className="card-header">
                <div className="dashboard-skeleton-line dashboard-skeleton-line-heading"></div>
              </div>
              <div className="card-body">
                <div className="dashboard-skeleton-chart"></div>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div className="card dashboard-skeleton-card">
              <div className="card-header">
                <div className="dashboard-skeleton-line dashboard-skeleton-line-heading"></div>
              </div>
              <div className="card-body">
                <div className="dashboard-skeleton-chart"></div>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div className="card dashboard-skeleton-card">
              <div className="card-header">
                <div className="dashboard-skeleton-line dashboard-skeleton-line-heading"></div>
              </div>
              <div className="card-body">
                <div className="dashboard-skeleton-chart"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Unable to load dashboard data. Please try refreshing the page.
        </div>
      </div>
    );
  }

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

  return (
    <div className="container-fluid">
      <div className="row dashboard-stats">
        <div className="col-md-2 col-sm-4 col-6 mb-3">
          <div className={`card h-100 ${activeFilter === 'all' ? 'border-primary' : ''}`}>
            <div className="card-body text-center p-3">
              <i className="fas fa-boxes fa-lg text-primary mb-1"></i>
              <h5 className="text-primary mb-1">{stats.totalAssets}</h5>
              <small className="card-text">Total Assets</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-sm-4 col-6 mb-3">
          <div className={`card h-100 ${activeFilter === 'available' ? 'border-success' : ''}`}>
            <div className="card-body text-center p-3">
              <i className="fas fa-check-circle fa-lg text-success mb-1"></i>
              <h5 className="text-success mb-1">{stats.availableAssets}</h5>
              <small className="card-text">Available</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-sm-4 col-6 mb-3">
          <div className={`card h-100 ${activeFilter === 'assigned' ? 'border-warning' : ''}`}>
            <div className="card-body text-center p-3">
              <i className="fas fa-user-check fa-lg text-warning mb-1"></i>
              <h5 className="text-warning mb-1">{stats.assignedAssets}</h5>
              <small className="card-text">Assigned</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-sm-4 col-6 mb-3">
          <div className={`card h-100 ${activeFilter === 'repair' ? 'border-danger' : ''}`}>
            <div className="card-body text-center p-3">
              <i className="fas fa-wrench fa-lg text-danger mb-1"></i>
              <h5 className="text-danger mb-1">{stats.inRepairAssets}</h5>
              <small className="card-text">In Repair</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-sm-4 col-6 mb-3">
          <div className="card h-100">
            <div className="card-body text-center p-3">
              <i className="fas fa-tools fa-lg text-info mb-1"></i>
              <h5 className="text-info mb-1">{stats.totalRepairs}</h5>
              <small className="card-text">Total Repairs</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-sm-4 col-6 mb-3">
          <div className="card h-100">
            <div className="card-body text-center p-3">
              <i className="fas fa-users fa-lg text-secondary mb-1"></i>
              <h5 className="text-secondary mb-1">{stats.totalEmployees}</h5>
              <small className="card-text">Employees</small>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-chart-bar me-1"></i>Overview
          </button>
        </li>
        {hasPermission('canManageRepairs') && (
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'repairs' ? 'active' : ''}`}
              onClick={() => setActiveTab('repairs')}
            >
              <i className="fas fa-wrench me-1"></i>Repair Analytics
            </button>
          </li>
        )}
      </ul>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-chart-bar me-2"></i>Assets by Type</h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.assetTypeData || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value} assets`, 'Quantity']} />
                      <Bar dataKey="value" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-chart-pie me-2"></i>Repair Status</h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Completed', value: stats.totalRepairs - stats.inRepairAssets, fill: '#28a745' },
                          { name: 'In Progress', value: stats.inRepairAssets, fill: '#dc3545' }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        dataKey="value"
                      >
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} repairs`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-chart-line me-2"></i>Monthly Assignment Trends</h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlyAssignments || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value} assignments`, 'Count']} />
                      <Line type="monotone" dataKey="assignments" stroke="#667eea" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-ban me-2 text-danger"></i>Dead Stock Assets</h5>
                </div>
                <div className="card-body">
                  {(stats.deadStockData || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.deadStockData || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip formatter={(value, name, props) => [`${value} units`, `${props.payload.assetType}`]} />
                        <Bar dataKey="deadCount" fill="#dc3545" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <i className="fas fa-check-circle fa-2x mb-2"></i>
                      <p className="mb-0">No dead stock assets recorded.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Repair Analytics Tab */}
      {activeTab === 'repairs' && repairAnalyticsLoading && (
        <div className="row">
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card dashboard-skeleton-card"><div className="card-body p-3"><div className="dashboard-skeleton-line dashboard-skeleton-line-value"></div><div className="dashboard-skeleton-line dashboard-skeleton-line-label mt-2"></div></div></div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card dashboard-skeleton-card"><div className="card-body p-3"><div className="dashboard-skeleton-line dashboard-skeleton-line-value"></div><div className="dashboard-skeleton-line dashboard-skeleton-line-label mt-2"></div></div></div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card dashboard-skeleton-card"><div className="card-body p-3"><div className="dashboard-skeleton-line dashboard-skeleton-line-value"></div><div className="dashboard-skeleton-line dashboard-skeleton-line-label mt-2"></div></div></div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card dashboard-skeleton-card"><div className="card-body p-3"><div className="dashboard-skeleton-line dashboard-skeleton-line-value"></div><div className="dashboard-skeleton-line dashboard-skeleton-line-label mt-2"></div></div></div>
          </div>
        </div>
      )}

      {activeTab === 'repairs' && repairAnalytics && (
        <>
          {/* Repair Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <i className="fas fa-rupee-sign fa-lg text-danger mb-2"></i>
                  <h5 className="text-danger mb-1">₹{repairAnalytics.summary.totalRepairCost.toLocaleString()}</h5>
                  <small className="text-muted">Total Repair Cost</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <i className="fas fa-tools fa-lg text-warning mb-2"></i>
                  <h5 className="text-warning mb-1">{repairAnalytics.summary.totalRepairs}</h5>
                  <small className="text-muted">Total Repairs</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <i className="fas fa-calculator fa-lg text-info mb-2"></i>
                  <h5 className="text-info mb-1">₹{repairAnalytics.summary.avgRepairCost.toLocaleString()}</h5>
                  <small className="text-muted">Avg Repair Cost</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <i className="fas fa-calendar-month fa-lg text-success mb-2"></i>
                  <h5 className="text-success mb-1">₹{repairAnalytics.summary.currentMonthCost.toLocaleString()}</h5>
                  <small className="text-muted">This Month</small>
                </div>
              </div>
            </div>
          </div>

          {/* Repair Charts */}
          <div className="row">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-chart-line me-2"></i>Monthly Repair Costs</h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={repairAnalytics.monthlyCosts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="monthName" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Cost']} />
                      <Line type="monotone" dataKey="totalCost" stroke="#667eea" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-chart-pie me-2"></i>Top 5 Expensive Assets</h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={repairAnalytics.assetCosts.slice(0, 5)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ assetName, totalRepairCost }) => `${assetName}: ₹${totalRepairCost.toLocaleString()}`}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="totalRepairCost"
                      >
                        {repairAnalytics.assetCosts.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Repair Cost']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Top Expensive Assets Table */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-list me-2"></i>Top 10 Most Expensive Assets to Repair</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Asset Name</th>
                          <th>Type</th>
                          <th>Total Cost</th>
                          <th>Repairs</th>
                          <th>Avg Cost</th>
                          <th>Last Repair</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repairAnalytics.assetCosts.slice(0, 10).map((asset, index) => (
                          <tr key={index}>
                            <td>{asset.assetName}</td>
                            <td><span className="badge bg-secondary">{asset.assetType}</span></td>
                            <td><strong className="text-danger">₹{asset.totalRepairCost.toLocaleString()}</strong></td>
                            <td>{asset.repairCount}</td>
                            <td>₹{asset.avgRepairCost.toLocaleString()}</td>
                            <td>{asset.lastRepairDate ? new Date(asset.lastRepairDate).toLocaleDateString() : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
